import _ from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Edge, XYPosition} from 'reactflow';

import {
  DecisionTreeNode,
  DecisionTreeNodeTypesEnum,
} from '../../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {
  isBranchNodeDataFrontChat,
  NodeDataFrontChatTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataFrontChatModel';
import {useDecisionTreeStore} from '../../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/hooks/useDecisionTreeStore';
import {useFlowBuilderDirtyStore} from '../../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/hooks/useFlowBuilderDirtyStore';
import {useFlowBuilderHistory} from '../../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/hooks/useFlowBuilderHistory';
import {ApplicationsState} from '../../../../settings/customFields/useApplications';
import {CustomFields} from '../../../../settings/editors/commons/customFieldsModel';
import {PreExpression} from '../../../../settings/editors/expressions/expressionModel';
import {ChatBotResult} from '../../../../settings/editors/expressions/infer/internal';
import {applyDirtyExpressionsEditorActions} from '../../flowBuilder/expressionHelpers';
import {useFlowBuilderExpressionsEditor} from '../../flowBuilder/expressionHooks';
import {makeChatBotBuilderNode, maybeUpdatePromptReference} from '../helpers';
import {
  ChatBotBuilderNodeData,
  ChatBotBuilderSelectedNode,
  ChatBotBuilderState,
  ChatBotBuilderStore,
} from '../types';

/*
 * Hooks.
 */

/**
 * Hook for retrieving the Decision Tree node and edge controlled states and helpers for their management.
 */
export function useChatBotBuilderStore(
  initialNodes: Array<DecisionTreeNode<ChatBotBuilderNodeData>>,
  initialEdges: Array<Edge>,
  initialExpressions: ReadonlyArray<PreExpression>,
  customFields: CustomFields,
  applicationsState: ApplicationsState,
): ChatBotBuilderStore {
  const decisionTreeStore = useDecisionTreeStore<ChatBotBuilderNodeData>(initialNodes, initialEdges);

  // We use a ref to ensure that the `deleteNode` method is stable for the history stack.
  const deleteNodeRef = useRef(decisionTreeStore.deleteNode);
  useEffect(() => {
    deleteNodeRef.current = decisionTreeStore.deleteNode;
  });

  const [selectedNode, setSelectedNode] = useState<ChatBotBuilderSelectedNode | undefined>();

  const expressionsEditor = useFlowBuilderExpressionsEditor(initialExpressions, ChatBotResult, {
    applicationsState,
    customFields,
  });

  // Store state slices in state for any change to allow popping slices off from most recent to older.
  const [currentStateSlice, setCurrentStateSlice] = useState<ChatBotBuilderState>({
    nodes: decisionTreeStore.nodes,
    edges: decisionTreeStore.edges,
    expressions: expressionsEditor.state.expressions,
  });
  useEffect(() => {
    // Wait until transitioning is complete before storing a state slice.
    if (decisionTreeStore.isTransitioning) {
      return;
    }

    // We do not want changes in the native React Flow "selected" property to trigger a re-computation.
    const decisionTreeStoreNodes = decisionTreeStore.nodes.map(({selected, ...node}) => node);
    const currentStateSliceNodes = currentStateSlice.nodes.map(({selected, ...node}) => node);

    if (
      _.isEqual(decisionTreeStoreNodes, currentStateSliceNodes) &&
      _.isEqual(decisionTreeStore.edges, currentStateSlice.edges) &&
      _.isEqual(expressionsEditor.state.expressions, currentStateSlice.expressions)
    ) {
      return;
    }

    setCurrentStateSlice({
      nodes: decisionTreeStore.nodes,
      edges: decisionTreeStore.edges,
      expressions: expressionsEditor.state.expressions,
    });
  }, [
    currentStateSlice.edges,
    currentStateSlice.expressions,
    currentStateSlice.nodes,
    decisionTreeStore.edges,
    decisionTreeStore.nodes,
    expressionsEditor.state.expressions,
    decisionTreeStore.isTransitioning,
  ]);

  // This callback must be stable in order for history to be stable.
  const onReplaceExpressionsRef = useRef(expressionsEditor.actions.onReplaceExpressions);
  const historyNavCallback = useCallback((poppedStateSlice: ChatBotBuilderState) => {
    // Set expressions to the previous state.
    onReplaceExpressionsRef.current(poppedStateSlice.expressions);
  }, []);

  const history = useFlowBuilderHistory(decisionTreeStore, currentStateSlice, historyNavCallback);

  const dirtyStore = useFlowBuilderDirtyStore(
    decisionTreeStore,
    isBranchNodeDataFrontChat,
    makeEndNodeAtPosition,
  );
  const {setIsDirty, setNodesWithDirty} = dirtyStore;

  const deletePromptNodeWithDirty = (nodeId: string) => {
    setIsDirty(true);
    history.push();

    const mutatedTree = deleteNodeRef.current(nodeId);
    const nextNodes = mutatedTree.nodes.map((node) =>
      maybeUpdatePromptReference(node, mutatedTree.nodes, mutatedTree.edges),
    );

    // Set all nodes
    setNodesWithDirty(nextNodes);
  };

  const [nodeIdsWithErrors, setNodeIdsWithErrors] = useState<ReadonlyArray<string>>([]);

  return {
    ...decisionTreeStore,
    selectedNode,
    setSelectedNode,
    expressionsEditor: applyDirtyExpressionsEditorActions(expressionsEditor, setIsDirty, history.push),
    history,
    ...dirtyStore,
    deletePromptNodeWithDirty,
    nodeIdsWithErrors,
    setNodeIdsWithErrors,
  };
}

/*
 * Helpers.
 */

function makeEndNodeAtPosition(position: XYPosition) {
  return makeChatBotBuilderNode(DecisionTreeNodeTypesEnum.PLACEHOLDER, position, {
    type: NodeDataFrontChatTypesEnum.END,
  });
}
